import { Component } from '@verndale/core';
import { animate, timeline } from 'motion';
import { debounce, keyCode } from '../../helpers';

class PrimaryNavigationNew extends Component {
  setupDefaults() {
    this.dom = {
      megamenuWrapper: this.el.querySelector(
        '.primary-navigation__megamenu-wrapper'
      ),
      megamenuPrimaryItems: this.el.querySelectorAll(
        '.primary-navigation__megamenu-item'
      ),
      megamenuPrimaryTriggers: this.el.querySelectorAll(
        '.primary-navigation__megamenu-item-primary-trigger'
      ),
      megamenuPanelsWrapper: this.el.querySelector(
        '.primary-navigation__megamenu-panels-wrapper'
      ),
      megamenuSecondaryPanels: this.el.querySelectorAll(
        '.primary-navigation__megamenu-secondary-panel'
      ),
      megamenuSecondaryTriggers: this.el.querySelectorAll(
        '.primary-navigation__megamenu-item-secondary-trigger'
      ),
      megamenuSecondaryPanelBackButtons: this.el.querySelectorAll(
        '.primary-navigation__megamenu-back-button'
      ),
      megamenuFirstPanelLinks: this.el.querySelectorAll(
        '.primary-navigation__megamenu-utility-links, .primary-navigation__megamenu-quick-links'
      ),
      megamenuDefaultMainResources: this.el.querySelectorAll(
        '.primary-navigation__megamenu-main-item-resources'
      ),
      megamenuSecondaryItemResources: this.el.querySelectorAll(
        '.primary-navigation__megamenu-item-resources'
      ),
      utilityLinksWrapper: this.el.querySelector(
        '.primary-utility__megamenu-links-wrapper'
      ),
      socialLinksWrapper: this.el.querySelector(
        '.primary-navigation__megamenu-social-links-wrapper'
      ),
      searchPanel: this.el.querySelector('.primary-navigation__megamenu-search'),
      backToMenuLabel: JSON.parse(this.el.getAttribute('data-labels')).backToMenu,
      backToResourcesLabel: JSON.parse(this.el.getAttribute('data-labels')).backToResources
    };

    this.el.style = '--megamenu-background-color: var(--rose-bud-cherry);';

    this.handleResize();
    this.hasMegamenuExpanded = false;

  }

  addListeners() {
    this.dom.megamenuPrimaryTriggers.forEach(t => {
      t.addEventListener('click', this.handlePrimaryTriggerClick.bind(this));
    });
    this.dom.megamenuSecondaryTriggers.forEach(t => {
      t.addEventListener('click', this.handleSecondaryTriggerClick.bind(this));
    });
    
    this.dom.megamenuSecondaryPanelBackButtons.forEach(b => {
      b.addEventListener('click', this.handleBackButtonClick.bind(this));
    });

    this.dom.megamenuSecondaryPanels.forEach(p => {
      p.addEventListener('keydown', this.handleTopButtonFocus.bind(this));
    });

    window.addEventListener('resize', debounce(this.handleResize.bind(this)));
  }

  getWrapper() {
    return this.dom.megamenuWrapper;
  }

  scrollToTop() {
    this.dom.megamenuSecondaryPanels.forEach(p => {
      p.scrollTop = 0;
    });
  }

  toggleMenu() {
    if (!this.dom.megamenuWrapper.classList.contains('active')) {
      this.dom.megamenuWrapper.classList.add('active');
      this.hasMegamenuExpanded = true;
    } else {
      this.hasMegamenuExpanded = false;
      this.dom.megamenuWrapper.classList.remove('active');
      this.closeAllPanels();
    }
    
  }

  closeMenu() {
    this.dom.megamenuWrapper.classList.remove('active');
    this.hasMegamenuExpanded = false;
    this.closeAllPanels();
  }

  openMenu() {
    this.dom.megamenuWrapper.classList.add('active');
    this.hasMegamenuExpanded = true;
  }

  expandPanelWrapper(el) {    
    let targetWidth = '100%';
    targetWidth = this.isTabletLandscape ? '50%' : targetWidth;

    let targetTransform = this.isMobile
      ? 'translate(0)'
      : 'translate(0)';

    targetTransform = this.isTablet ? 'translate(0, 0)' : targetTransform;
    this.dom.megamenuSecondaryPanels.forEach(p => p.classList.remove('active'));
    const secondaryPanel = [...this.dom.megamenuSecondaryPanels].find(
      p => p.id === el.getAttribute('aria-controls')
    );
    secondaryPanel.classList.add('active');

    if (this.isMobile) {
      this.el.style = '--megamenu-background-color: transparent;';
    }

    animate(
      this.dom.megamenuPanelsWrapper,
      {
        width: targetWidth,
        transform: targetTransform
      },
      { duration: 0, delay: 0, ease: 'easeInOut' }
    ).finished.then(() => {
      this.dom.megamenuPanelsWrapper?.classList.add('active');

      if (this.isMobile) {
        this.el.style = '--megamenu-background-color: var(--mexican-red);';
        this.dom.megamenuPrimaryTriggers.forEach(t => {
          t.setAttribute('tabindex', '-1');
        });
        this.dom.megamenuFirstPanelLinks.forEach(ul => {
          ul.style.visibility = 'hidden';
        });
      }
      this.dom.megamenuPrimaryTriggers.forEach(t =>
        t.setAttribute('aria-expanded', 'false')
      );
      el.setAttribute('aria-expanded', 'true');
      
      // focus to the secondary panel if it is a primary trigger  
      if (!el.parentNode.classList.contains('primary-navigation__megamenu-item--resource')) {
        secondaryPanel.querySelector('.primary-navigation__megamenu-item-secondary-trigger').focus();
      }

      const secondaryPanelActiveList = secondaryPanel.querySelector('.primary-navigation__megamenu-secondary-items');
      if (secondaryPanelActiveList && secondaryPanelActiveList.children.length > 15) {
        secondaryPanelActiveList.classList.add('two-columns');
      }
    });
  }

  collapsePanelWrapper(el) {

    const activeSecondaryPanel = [...this.dom.megamenuSecondaryPanels].find(p =>
      p.classList.contains('active')
    );
    animate(
      this.dom.megamenuPanelsWrapper,
      {
        transform: 'translate(0, 0)',
        inlineSize: this.isMobile || this.isTablet ? '' : '33.3%'
      },
      { duration: 0, delay: 0, ease: 'easeInOut' }
    ).finished.then(() => {
      this.dom.megamenuPanelsWrapper?.classList.remove('active');
      el.removeAttribute('aria-current');
      this.dom.megamenuPrimaryTriggers.forEach(t =>
        t.setAttribute('aria-expanded', 'false')
      );
      this.dom.megamenuSecondaryTriggers.forEach(t =>
        t.setAttribute('aria-expanded', 'false')
      );
      this.dom.megamenuSecondaryItemResources.forEach(r =>
        r.classList.remove('active')
      );
      this.dom.megamenuDefaultMainResources.forEach(r => {
        r.children.forEach(c => {
          c.style = null;
        });
      });
      if (activeSecondaryPanel) {
        animate(
          activeSecondaryPanel,
          { opacity: 0 },
          { duration: 0 }
        ).finished.then(() => {
          activeSecondaryPanel.classList.remove('active');
          activeSecondaryPanel.style = null;
        });
      } else activeSecondaryPanel.classList.remove('active');
    });
  }

  showSecondaryResourceItems(el) {   
    const itemsPanel = [...this.dom.megamenuSecondaryItemResources].find(
      p => p.id === el.getAttribute('aria-controls')
    );
    const possibleActivePanel = [
      ...this.dom.megamenuSecondaryItemResources
    ].find(p => p.classList.contains('active'));
    const possibleActiveTrigger = [...this.dom.megamenuSecondaryTriggers].find(
      t => t.getAttribute('aria-expanded') === 'true'
    );

    const itemPanelClosestItems = itemsPanel.closest(
      '.primary-navigation__megamenu-secondary-items'
    );

    const itemPanelClosestTitle = itemPanelClosestItems.previousElementSibling;

   const clickedContent = el.querySelector('span').textContent;
   
   this.dom.megamenuSecondaryPanelBackButtons.forEach(b => {
    if(this.dom.backToResourcesLabel)
      b.textContent = this.dom.backToResourcesLabel;
    else
        b.textContent = 'Back to resources for';
    }); 

      itemPanelClosestTitle.textContent = `${clickedContent}`;

    const sequence = [
      [
        itemPanelClosestItems,
        { opacity: 1 },
      ],
      [
        itemsPanel,
        { transform: 'translate(0, 0)' },
        { duration: 0, delay: 0, ease: 'none' }
      ]
    ];

    if (this.isMobile) {
      animate(
        [
          // mainItemDefaultResources,
          itemPanelClosestItems,
          itemPanelClosestTitle
        ],
      ).finished.then(() => {
        itemsPanel.classList.add('active');
        if (this.isMobile) itemPanelClosestItems.classList.add('active');
        // itemPanelClosestTitle.classList.add('hidden');
        timeline(sequence).finished.then(() => {
          // mainItemDefaultResources.style.display = 'none';
          el.setAttribute('aria-expanded', 'true');
        });
      });
    } else {
      const sequenceActivePanel = [
        [
          possibleActivePanel,
          {
            opacity: 0,

          },
          { duration: 0, delay: 0, ease: 'easeInOut' }
        ]
      ];
      const sequenceItemsPanel = [
        [
          itemsPanel,
          {
            opacity: [0, 1],
          },
          { duration: 0, delay: 0, ease: 'easeInOut' }
        ]
      ];
      timeline(sequenceActivePanel)
        .finished.then(() => {
          possibleActivePanel?.classList.remove('active');
          possibleActiveTrigger?.setAttribute('aria-expanded', 'false');
        })
        .then(() => {
          itemsPanel.classList.add('active');
          timeline(sequenceItemsPanel);
        });
    }
  }

  hideSecondaryResourceItems(el) {
    const itemsPanel = [...this.dom.megamenuSecondaryItemResources].find(
      p => p.id === el.getAttribute('aria-controls')
    );
    const itemPanelClosestItems = itemsPanel.closest(
      '.primary-navigation__megamenu-secondary-items'
    );

    if (this.isMobile) {
      itemPanelClosestItems.classList.remove('active');
      itemsPanel?.classList.remove('active');
      el.setAttribute('aria-expanded', 'false');
    } else {
      const sequenceItemsPanel = [
        [
          itemsPanel,
          {
            opacity: [1, 0],
          },
          { duration: 0, delay: 0, ease: 'easeInOut' }
        ]
      ];

      timeline(sequenceItemsPanel)
        .finished.then(() => {
          itemPanelClosestItems.classList.remove('active');
          itemsPanel?.classList.remove('active');
        });
    }
  }

  closeAllPanels() {
    this.el.style = '--megamenu-background-color: var(--rose-bud-cherry);';
    this.dom.megamenuPanelsWrapper?.classList.remove('active');
    this.dom.megamenuPanelsWrapper.style = null;

    this.dom.megamenuPrimaryTriggers.forEach(t =>
      t.setAttribute('aria-expanded', 'false')
    );
    this.dom.megamenuSecondaryTriggers.forEach(t =>
      t.setAttribute('aria-expanded', 'false')
    );
    this.dom.megamenuSecondaryItemResources.forEach(p =>
      p.classList.remove('active')
    );
    this.dom.megamenuSecondaryPanels.forEach(p => {
      const secondaryItemResources = p.querySelector(
        '.primary-navigation__megamenu-secondary-items.active'
      );
      if (secondaryItemResources) {
        secondaryItemResources.style.opacity = 1;
        secondaryItemResources.classList.remove('active');
        p.querySelectorAll('.hidden').forEach(h => {
          h.classList.remove('hidden');
          h.style.opacity = '1';
          const mainItemDefaultResources = p.querySelector(
            '.primary-navigation__megamenu-secondary-items'
          ).nextElementSibling;
          mainItemDefaultResources.style.display = 'flex';
          mainItemDefaultResources.style.opacity = 1;
        });
      }
      p.classList.remove('active');
    });
    this.dom.megamenuPrimaryTriggers.forEach(t => {
      t?.setAttribute('tabindex', '0');
    });
    this.dom.megamenuFirstPanelLinks.forEach(ul => {
      ul.style.visibility = 'visible';
    });
    [...this.dom.megamenuDefaultMainResources].forEach(m => {
      m.children.forEach(c => {
        c.style = null;
      });
    });
  }

  handlePrimaryTriggerClick(e) {
    // Disable news panel
    const news = document.querySelector('.primary-navigation__megamenu-news');
    if (news)
    news.classList.add('disabled');

    if (e.currentTarget.getAttribute('aria-expanded') === 'true') {
      this.collapsePanelWrapper(e.currentTarget);
    } else {
      this.expandPanelWrapper(e.currentTarget);
    }

    this.dom.megamenuSecondaryPanelBackButtons.forEach(b => {
      if(this.dom.backToMenuLabel)
        b.textContent = this.dom.backToMenuLabel;
      else
        b.textContent = 'Back to main menu';
    });
  }

  handleSecondaryTriggerClick(e) {  
    if (e.currentTarget.getAttribute('aria-expanded') === 'true') {
      this.hideSecondaryResourceItems(e.currentTarget);
    } else {
      this.showSecondaryResourceItems(e.currentTarget);
    }
  }

  handleTopButtonFocus(e) {
    if (e.keyCode === keyCode.TAB) {
      if (e.target.getBoundingClientRect().top < 10) {
        e.currentTarget.scrollTop = 0;
      }
    }
  }

  handleBackButtonClick(e) {
    const { parentNode } = e.target;
    const secondaryItemResources = e.currentTarget.parentNode.querySelector(
      '.primary-navigation__megamenu-secondary-items.active'
    );
    const itemsPanel = parentNode.querySelector(
      '.primary-navigation__megamenu-item-resources.active'
    );

    if (secondaryItemResources === null) {

      this.el.style = '--megamenu-background-color: transparent;';
      this.dom.megamenuPrimaryTriggers.forEach(t => {
        t.setAttribute('tabindex', '0');
      });
      this.dom.megamenuFirstPanelLinks.forEach(ul => {
        ul.style.visibility = 'visible';
      });
      animate(
        this.dom.megamenuPanelsWrapper,
        { transform: 'translate(0, 0)' },
        { duration: 0, delay: 0, ease: 'easeInOut' }
      ).finished.then(() => {
        this.el.style =
          '--megamenu-background-color: var(--rose-bud-cherry);';
        parentNode.scrollTop = 0;
        parentNode.classList.remove('active');
        this.dom.megamenuPanelsWrapper?.classList.remove('active');
        this.dom.megamenuPrimaryTriggers.forEach(t => {
          t.removeAttribute('aria-current');
          t.setAttribute('aria-expanded', 'false');
        });
        this.dom.megamenuPrimaryTriggers[0].focus();
      });
    } else {

      const textContent = "Resources for"
      const itemParent = e.currentTarget.parentNode;
      itemParent.querySelector('span').textContent = textContent;

      this.dom.megamenuSecondaryPanelBackButtons.forEach(b => {
          b.textContent = 'Back to main  menu';
      }); 

      const itemPanelClosestTitle = e.target.nextElementSibling;

      animate(
        itemsPanel,
        { transform: 'translate(100%, 0)' },
        { duration: 0, delay: 0, ease: 'none' }
      ).finished.then(() => {
        itemPanelClosestTitle.classList.remove('hidden');
        secondaryItemResources.classList.remove('active');
        animate(
          [
            itemPanelClosestTitle,
            secondaryItemResources,
           // mainItemDefaultResources
          ],
          { opacity: 1 },
          { duration: 0.15, delay: 0, ease: 'easeInOut' }
        ).finished.then(() => {
          itemsPanel?.classList.remove('active');
          this.dom.megamenuSecondaryTriggers.forEach(t =>
            t.setAttribute('aria-expanded', 'false')
          );
        });
      });
    }
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.matchMedia('(max-width: 1023px)').matches;
    this.isTablet = this.isMobile;
    this.isTabletLandscape = window.matchMedia('(max-width: 4440px)').matches;

    if (wasMobile !== this.isMobile) {
      this.el.style = '--megamenu-background-color: var(--rose-bud-cherry);';
    }
  }
}

export default PrimaryNavigationNew;
